import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import "./Testimonials.css";
import { ENDPOINTS } from "../api/api";
import useFetch from "../hooks/useFetch";
import { useTranslation } from "react-i18next";
import "../i18n";

const StarRating = ({ rating }) => (
  <div className="tm-stars">
    {[...Array(5)].map((_, i) => (
      <i key={i} className={`fa-star ${i < rating ? "fa-solid" : "fa-regular"}`}></i>
    ))}
  </div>
);

const TestimonialCard = ({ testimonial, featured }) => {
  const [hovered,  setHovered]  = useState(false);
  const [imgError, setImgError] = useState(false);

  return (
    <div
      className={`tm-card ${featured ? "tm-card--featured" : ""} ${hovered ? "tm-card--hovered" : ""}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="tm-quote-icon"><i className="fa-solid fa-quote-left"></i></div>
      <span className="tm-category">{testimonial.category}</span>
      <StarRating rating={testimonial.rating} />
      <p className="tm-feedback">"{testimonial.feedback}"</p>
      <div className="tm-client">
        <div className="tm-client-avatar" style={{ background: `linear-gradient(135deg, ${testimonial.color}, #032d27)` }}>
          {testimonial.image && !imgError ? (
            <img src={testimonial.image} alt={testimonial.client_name} onError={() => setImgError(true)}
              style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%", display: "block" }} />
          ) : (
            <span>{testimonial.initials}</span>
          )}
        </div>
        <div className="tm-client-info">
          <span className="tm-client-name">{testimonial.client_name}</span>
          <span className="tm-client-position">{testimonial.position}</span>
        </div>
      </div>
    </div>
  );
};

const Testimonials = () => {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState("All");

  const { data: testimonials, loading: loadingTests } = useFetch(ENDPOINTS.testimonials);
  const { data: stats,        loading: loadingStats  } = useFetch(ENDPOINTS.testimonialStats);

  const TESTIMONIALS = testimonials || [];
  const STATS        = stats        || [];

  const CATEGORIES = ["All", ...Array.from(new Set(TESTIMONIALS.map((t) => t.category)))];
  const featured = TESTIMONIALS.filter((t) => t.featured);
  const filtered = activeCategory === "All"
    ? TESTIMONIALS
    : TESTIMONIALS.filter((t) => t.category === activeCategory);

  const isLoading = loadingTests || loadingStats;

  return (
    <div className="testimonials-page">

      {/* ── Hero ── */}
      <section className="tm-hero">
        <div className="tm-hero-bg">
          <div className="tm-orb tm-orb--1"></div>
          <div className="tm-orb tm-orb--2"></div>
          <div className="tm-hero-grid"></div>
        </div>
        <div className="tm-hero-content">
          <span className="tm-eyebrow">
            <i className="fa-solid fa-star"></i>
            {t("testimonials.eyebrow")}
          </span>
          <h1 className="tm-hero-title">
            {t("testimonials.title")}
            <span className="tm-hero-highlight"> {t("testimonials.highlight")}</span>
          </h1>
          <p className="tm-hero-subtitle">{t("testimonials.subtitle")}</p>
          <div className="tm-hero-rating">
            <div className="tm-hero-stars">
              {[...Array(5)].map((_, i) => <i key={i} className="fa-solid fa-star"></i>)}
            </div>
            <span className="tm-hero-rating-text">{t("testimonials.rating")}</span>
          </div>
        </div>
      </section>

      {/* ── Stats Bar ── */}
      <section className="tm-stats">
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "2rem", width: "100%" }}>
            <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: "1.5rem", opacity: 0.5 }}></i>
          </div>
        ) : (
          STATS.map((s) => (
            <div key={s.id} className="tm-stat-item">
              <span className="tm-stat-value">{s.value}</span>
              <span className="tm-stat-label">{s.label}</span>
            </div>
          ))
        )}
      </section>

      {/* ── Featured Testimonials ── */}
      {!isLoading && featured.length > 0 && (
        <section className="tm-featured-section">
          <div className="tm-section-header">
            <span className="tm-section-eyebrow">{t("testimonials.topReviews")}</span>
            <h2 className="tm-section-title">{t("testimonials.highlighted")}</h2>
          </div>
          <div className="tm-featured-grid">
            {featured.map((t) => <TestimonialCard key={t.id} testimonial={t} featured={true} />)}
          </div>
        </section>
      )}

      {/* ── Filter + All Testimonials ── */}
      {!isLoading && (
        <section className="tm-all-section">
          <div className="tm-section-header">
            <span className="tm-section-eyebrow">{t("testimonials.allReviews")}</span>
            <h2 className="tm-section-title">{t("testimonials.allVoices")}</h2>
            <p className="tm-section-subtitle">{t("testimonials.allSubtitle")}</p>
          </div>
          <div className="tm-filter-bar">
            {CATEGORIES.map((cat) => (
              <button key={cat}
                className={`tm-filter-btn ${activeCategory === cat ? "tm-filter-btn--active" : ""}`}
                onClick={() => setActiveCategory(cat)}>
                {cat}
              </button>
            ))}
          </div>
          <div className="tm-cards-grid">
            {filtered.map((t) => <TestimonialCard key={t.id} testimonial={t} featured={false} />)}
          </div>
        </section>
      )}

      {isLoading && (
        <div style={{ textAlign: "center", padding: "8rem 0" }}>
          <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: "2.5rem", opacity: 0.5 }}></i>
        </div>
      )}

      {/* ── Trust Badges ── */}
      <section className="tm-trust-section">
        <div className="tm-section-header">
          <span className="tm-section-eyebrow">{t("testimonials.trustEyebrow")}</span>
          <h2 className="tm-section-title">{t("testimonials.trustTitle")}</h2>
        </div>
        <div className="tm-trust-grid">
          {[
            { icon: "fa-solid fa-shield-halved", title: "Transparent Always",  desc: "No surprises — clear communication, honest timelines, and regular progress updates throughout every project." },
            { icon: "fa-solid fa-handshake",     title: "Partnership Mindset", desc: "We treat every client's problem as our own. Your success is the only metric we care about." },
            { icon: "fa-solid fa-clock",         title: "Delivery on Time",    desc: "100% of our projects delivered on or ahead of schedule. We plan carefully and execute with discipline." },
            { icon: "fa-solid fa-arrow-trend-up",title: "Built to Scale",      desc: "Everything we build is architected for growth — not just for today, but for where your business is going." },
          ].map((item) => (
            <div key={item.title} className="tm-trust-card">
              <div className="tm-trust-icon"><i className={item.icon}></i></div>
              <h3 className="tm-trust-title">{item.title}</h3>
              <p className="tm-trust-desc">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="tm-cta-section">
        <div className="tm-cta-inner">
          <div className="tm-cta-orb"></div>
          <span className="tm-section-eyebrow">
            <i className="fa-solid fa-handshake"></i>
            {t("testimonials.ctaEyebrow")}
          </span>
          <h2 className="tm-cta-title">{t("testimonials.ctaTitle")}</h2>
          <p className="tm-cta-sub">{t("testimonials.ctaSub")}</p>
          <div className="tm-cta-actions">
            <NavLink to="/contact" className="tm-btn-primary">
              <i className="fa-solid fa-paper-plane"></i>
              {t("testimonials.startConversation")}
            </NavLink>
            <NavLink to="/portfolio" className="tm-btn-secondary">
              {t("testimonials.seeOurWork")}
              <i className="fa-solid fa-arrow-right"></i>
            </NavLink>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Testimonials;